import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share } from "lucide-react";
import { motion } from "framer-motion";
import { RainbowButton } from "@/components/ui/rainbow-button";

interface CompletionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  completionTime: number;
  interestTopic: string;
  shareUrl: string;
  isMobile: boolean;
}

const CompletionDialog: React.FC<CompletionDialogProps> = ({
  isOpen,
  onClose,
  completionTime,
  interestTopic,
  shareUrl,
  isMobile
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Reset and show confetti when dialog opens
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
    } else {
      setShowConfetti(false);
    }
  }, [isOpen]);

  const handleShare = async () => {
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: 'Crossword Puzzle',
          text: `I completed a custom crossword \"${interestTopic}\" in ${formatTime(completionTime)}!`,
          url: shareUrl
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      await copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      setProgress(100);
      // Start the progress animation
      setTimeout(() => {
        setProgress(0);
      }, 50);
      // Reset after animation completes
      setTimeout(() => {
        setIsCopied(false);
        setProgress(0);
      }, 4000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  // Confetti colors
  const confettiColors = [
    "#9b87f5", // Primary Purple
    "#7E69AB", // Secondary Purple
    "#D6BCFA", // Light Purple
    "#8B5CF6", // Vivid Purple
    "#D946EF", // Magenta Pink
    "#F97316", // Bright Orange
    "#33C3F0", // Sky Blue
    "#FFD700", // Gold
    "#22c55e", // Green
  ];

  return (
    <>
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[9999]">
          {/* First confetti layer - slow falling pieces */}
          {Array.from({ length: isMobile ? 80 : 160 }).map((_, i) => (
            <motion.div 
              key={`slow-${i}`}
              className="absolute rounded-sm"
              initial={{ 
                top: -20, 
                left: `${Math.random() * 100}%`,
                rotate: 0,
                opacity: 1,
                backgroundColor: confettiColors[Math.floor(Math.random() * confettiColors.length)]
              }}
              animate={{
                top: '110vh',
                rotate: Math.random() > 0.5 ? 360 : -360,
                opacity: 0
              }}
              transition={{
                duration: Math.random() * 4 + 5,
                delay: Math.random() * 0.5,
                ease: [0.1, 0.4, 0.6, 1],
              }}
              style={{
                width: `${Math.random() * 12 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                filter: `brightness(${0.8 + Math.random() * 0.4})`,
              }}
            />
          ))}
          
          {/* Second confetti layer - fast falling pieces */}
          {Array.from({ length: isMobile ? 60 : 120 }).map((_, i) => (
            <motion.div 
              key={`fast-${i}`}
              className="absolute rounded-sm"
              initial={{ 
                top: -20, 
                left: `${Math.random() * 100}%`,
                rotate: 0,
                opacity: 1,
                backgroundColor: confettiColors[Math.floor(Math.random() * confettiColors.length)]
              }}
              animate={{
                top: '110vh',
                rotate: Math.random() > 0.5 ? 720 : -720,
                x: Math.random() > 0.5 ? Math.random() * 100 : Math.random() * -100,
                opacity: 0
              }}
              transition={{
                duration: Math.random() * 2 + 2,
                delay: Math.random() * 0.3,
                ease: "easeIn",
              }}
              style={{
                width: `${Math.random() * 8 + 3}px`,
                height: `${Math.random() * 6 + 3}px`,
                filter: `brightness(${0.9 + Math.random() * 0.3})`,
              }}
            />
          ))}

          {/* Larger special confetti pieces - stars, circles */}
          {Array.from({ length: isMobile ? 15 : 30 }).map((_, i) => {
            const isSpecial = Math.random() > 0.5;
            return (
              <motion.div 
                key={`special-${i}`}
                className={`absolute ${isSpecial ? 'rounded-full' : 'star'}`}
                initial={{ 
                  top: -30, 
                  left: `${Math.random() * 100}%`,
                  rotate: 0,
                  opacity: 1,
                  scale: 0
                }}
                animate={{
                  top: '110vh',
                  rotate: 360,
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1, 1, 0.5]
                }}
                transition={{
                  duration: Math.random() * 5 + 6,
                  delay: Math.random() * 1,
                  times: [0, 0.1, 0.9, 1],
                  ease: "easeOut"
                }}
                style={{
                  width: `${Math.random() * 15 + 10}px`,
                  height: `${Math.random() * 15 + 10}px`,
                  backgroundColor: confettiColors[Math.floor(Math.random() * confettiColors.length)],
                  boxShadow: `0 0 10px ${confettiColors[Math.floor(Math.random() * confettiColors.length)]}`,
                }}
              />
            );
          })}
        </div>
      )}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[400px] mx-auto my-4' : 'sm:max-w-md'}`}>
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">Congratulations! 🎉</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-2 text-center">
              <div className="text-sm text-green-800">Your time:</div>
              <motion.div 
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 15,
                  duration: 0.8 
                }}
                className="text-3xl font-bold text-primary"
              >
                {formatTime(completionTime)}
              </motion.div>
            </div>
            
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex justify-center"
            >
              <div className="relative w-20 h-20">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ 
                    scale: [0, 1.2, 1],
                    rotate: [0, 10, -10, 0]
                  }}
                  transition={{ 
                    duration: 0.8,
                    delay: 0.8,
                    times: [0, 0.6, 0.9, 1],
                    ease: "easeOut"
                  }}
                  className="absolute inset-0 flex justify-center items-center"
                >
                  <span className="text-5xl">🏆</span>
                </motion.div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="w-full text-center text-sm text-green-800 mt-0 mb-1 px-2"
          >
            Enjoyed your puzzle? 😊<br/>
            This site is powered by some pretty big brains (LLMs!) and isn't cheap to run. If you had fun, consider supporting with a coffee — it helps keep the crosswords coming & free!
          </motion.div>

          <DialogFooter className={`${isMobile ? 'flex flex-col justify-center items-center gap-3 mt-2' : 'flex flex-row justify-center items-center gap-4 mt-1'}`}>
            <div className={`${isMobile ? 'w-full' : 'w-[180px]'} h-12`}>
              <Button 
                onClick={handleShare} 
                className="w-full h-full flex items-center justify-center gap-2 bg-[#305CDE] hover:bg-[#000080] px-4 py-2 transition-all duration-300 ease-out relative overflow-hidden text-base font-semibold rounded-2xl !h-12 !py-2"
              >
                <div className="relative z-10 flex items-center justify-center gap-2 w-full">
                  {!isCopied && <Share className="h-4 w-4" />}
                  <span className={`transition-opacity duration-300 ${isCopied ? 'text-sm' : ''}`}>
                    {isCopied ? "URL Copied!" : "Share"}
                  </span>
                </div>
                {isCopied && (
                  <div 
                    className="absolute left-0 top-0 h-full bg-secondary/45"
                    style={{ 
                      width: `${progress}%`,
                      transition: 'width 4s ease-out'
                    }}
                  />
                )}
              </Button>
            </div>
            <a href="https://buymeacoffee.com/crossabledev" target="_blank" rel="noopener noreferrer" className={`${isMobile ? 'w-full' : 'w-[180px]'} h-12`}>
              <Button 
                className="w-full h-full px-4 py-2 text-base font-semibold !h-12 !px-4 !py-2 whitespace-nowrap bg-[#FADA7A] text-black hover:bg-[#ffe9b3] rounded-2xl"
              >
                ☕ Buy Me A Coffee
              </Button>
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompletionDialog;
